import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useOrdersList } from "@/hooks/useOrdersList";
import { useCommission } from "@/hooks/useCommission";
import { formatPersianNumber } from "@/lib/format/persian";

const PER_PAGE = 10;

export function useOrdersPage() {
  const { t } = useTranslation("orders");
  const ordersList = useOrdersList();
  const { isRestricted: isOrderCreateBlocked } = useCommission();

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      ordersList.setSearch(event.target.value);
    },
    [ordersList],
  );

  const paginationLabel = useMemo(() => {
    if (!ordersList.meta) return "";
    const from = (ordersList.page - 1) * PER_PAGE + 1;
    const to = Math.min(ordersList.page * PER_PAGE, ordersList.meta.total);
    return t("pagination.showing", {
      from: formatPersianNumber(from),
      to: formatPersianNumber(to),
      total: formatPersianNumber(ordersList.meta.total),
    });
  }, [ordersList.meta, ordersList.page, t]);

  const countLabel = useMemo(() => {
    const total = ordersList.stats?.total_count ?? ordersList.meta?.total ?? 0;
    if (total === 0) return t("countLabel.empty");
    return t("countLabel.total", { count: formatPersianNumber(total) });
  }, [ordersList.meta?.total, ordersList.stats?.total_count, t]);

  return {
    ...ordersList,
    handleSearchChange,
    paginationLabel,
    countLabel,
    isOrderCreateBlocked,
  };
}
